import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { CreditCard, Plus, Receipt, AlertCircle, Calendar, FileText, ChevronRight, Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { format } from "date-fns";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { apiRequest } from "@/lib/queryClient";
import { capturePostHogEvent } from "@/lib/posthog";
import { ParentLayout } from "@/components/parent/ParentLayout";

const stripePromise = import.meta.env.VITE_STRIPE_PUBLIC_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY)
  : null;

interface ChildBilling {
  childId: string;
  firstName: string;
  lastName: string;
  grade: number;
  plan: string | null;
  subscriptionStatus: string;
  subscriptionEndDate: string | null;
  subscriptionPausedUntil: string | null;
  retentionSaveOfferShown: boolean;
  retentionPauseOfferShown: boolean;
  stripe: null | {
    id: string;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    currentPriceId: string | null;
    currentAmount: number | null;
    currentCurrency: string | null;
    pauseCollection: any;
  };
  card: null | { brand: string; last4: string; expMonth: number; expYear: number };
}

interface Invoice {
  id: string;
  number: string | null;
  status: string;
  amountPaid: number;
  currency: string;
  created: string | null;
  hostedUrl: string | null;
  pdfUrl: string | null;
}

function statusBadgeVariant(s: string): "default" | "secondary" | "destructive" | "outline" {
  if (s === "active" || s === "trial" || s === "trialing") return "default";
  if (s === "past_due" || s === "expired") return "destructive";
  return "outline";
}

function statusLabel(s: string): string {
  switch (s) {
    case "active": return "Active";
    case "trial":
    case "trialing": return "Trial";
    case "past_due": return "Payment failed";
    case "cancelled": return "Cancelled";
    case "expired": return "Expired";
    case "inactive": return "Inactive";
    case "paused": return "Paused";
    default: return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount == null || !currency) return "–";
  return new Intl.NumberFormat("en-JM", { style: "currency", currency: currency.toUpperCase(), minimumFractionDigits: 0 }).format(amount / 100);
}

export default function ParentBilling() {
  const { data, isLoading } = useQuery<{ children: ChildBilling[] }>({ queryKey: ["/api/parent/billing"] });

  const [addChildOpen, setAddChildOpen] = useState(false);

  return (
    <ParentLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Billing</h1>
            <p className="text-sm text-muted-foreground">Manage every child's subscription, payment method, and invoices.</p>
          </div>
          <Button onClick={() => setAddChildOpen(true)} data-testid="add-child-button">
            <Plus className="mr-2 h-4 w-4" />
            Add a child
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        ) : !data || data.children.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center space-y-3">
              <CreditCard className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="font-semibold">No subscriptions yet</p>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">Add a child to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {data.children.map((c) => <BillingCard key={c.childId} c={c} />)}
          </div>
        )}

        <AddChildDialog open={addChildOpen} onOpenChange={setAddChildOpen} />
      </div>
    </ParentLayout>
  );
}

function BillingCard({ c }: { c: ChildBilling }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [updateCardOpen, setUpdateCardOpen] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: async ({ reason, otherDetails }: { reason: string; otherDetails?: string }) => {
      await apiRequest("POST", `/api/parent/billing/subscriptions/${c.childId}/cancel`, { reason, otherDetails });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      capturePostHogEvent("parent_subscription_cancelled", { childId: c.childId, reason: vars.reason });
      toast({ title: "Subscription cancelled", description: "Access continues until the end of the current period." });
      setCancelOpen(false);
    },
    onError: (e: any) => toast({ title: "Could not cancel", description: e.message, variant: "destructive" }),
  });

  const saveOfferMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/parent/billing/subscriptions/${c.childId}/save-offer`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      capturePostHogEvent("parent_save_offer_accepted", { childId: c.childId });
      toast({ title: "40% off applied", description: `${c.firstName}'s next bill is 40% lower. Thanks for staying.` });
      setCancelOpen(false);
    },
    onError: (e: any) => toast({ title: "Could not apply offer", description: e.message, variant: "destructive" }),
  });

  const pauseMutation = useMutation({
    mutationFn: async (months: 1 | 2 | 3) => {
      await apiRequest("POST", `/api/parent/billing/subscriptions/${c.childId}/pause`, { months });
    },
    onSuccess: (_data, months) => {
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      capturePostHogEvent("parent_subscription_paused", { childId: c.childId, months });
      toast({ title: "Subscription paused", description: "Billing is paused. Access will resume automatically." });
      setCancelOpen(false);
    },
    onError: (e: any) => toast({ title: "Could not pause", description: e.message, variant: "destructive" }),
  });

  const resumeMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/parent/billing/subscriptions/${c.childId}/resume-from-pause`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      toast({ title: "Subscription resumed" });
    },
    onError: (e: any) => toast({ title: "Could not resume", description: e.message, variant: "destructive" }),
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/parent/billing/subscriptions/${c.childId}/reactivate`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      toast({ title: "Subscription reactivated" });
    },
    onError: (e: any) => toast({ title: "Could not reactivate", description: e.message, variant: "destructive" }),
  });

  const retryPayment = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/parent/billing/subscriptions/${c.childId}/retry-payment`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      toast({ title: "Payment retried successfully" });
    },
    onError: (e: any) => toast({ title: "Retry failed", description: e.message, variant: "destructive" }),
  });

  const isActive = c.subscriptionStatus === "active" || c.subscriptionStatus === "trial" || c.subscriptionStatus === "trialing";
  const isPastDue = c.subscriptionStatus === "past_due";
  const isPaused = c.subscriptionStatus === "paused";
  const isCancelling = c.stripe?.cancelAtPeriodEnd;
  const renewalDate = c.stripe?.currentPeriodEnd ? new Date(c.stripe.currentPeriodEnd) : null;
  const pausedUntil = c.subscriptionPausedUntil ? new Date(c.subscriptionPausedUntil) : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg">{c.firstName} {c.lastName}</CardTitle>
            <CardDescription>Grade {c.grade}{c.plan ? ` · ${c.plan}` : ""}</CardDescription>
          </div>
          <Badge variant={statusBadgeVariant(c.subscriptionStatus)} data-testid={`billing-status-${c.childId}`}>
            {statusLabel(c.subscriptionStatus)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isPastDue && (
          <div className="rounded-md border border-destructive bg-destructive/5 p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-destructive">Payment failed</p>
              <p className="text-muted-foreground">Update your card or retry the existing payment to restore access.</p>
              <Button size="sm" className="mt-2" onClick={() => retryPayment.mutate()} disabled={retryPayment.isPending}>
                {retryPayment.isPending ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                Retry payment
              </Button>
            </div>
          </div>
        )}

        {isCancelling && renewalDate && (
          <div className="rounded-md border bg-muted p-3 text-sm">
            Cancels on <strong>{format(renewalDate, "MMM d, yyyy")}</strong>. You will keep access until then.
          </div>
        )}

        {isPaused && pausedUntil && (
          <div className="rounded-md border bg-muted p-3 flex items-start gap-3 flex-wrap">
            <div className="text-sm flex-1 min-w-0">
              <p className="font-medium">Paused</p>
              <p className="text-muted-foreground">Billing resumes on {format(pausedUntil, "MMM d, yyyy")}. {c.firstName} keeps access during the pause.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => resumeMutation.mutate()} disabled={resumeMutation.isPending}>
              {resumeMutation.isPending ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
              Resume now
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span>
              {renewalDate
                ? `${isCancelling ? "Ends" : "Renews"} ${format(renewalDate, "MMM d, yyyy")}`
                : c.subscriptionEndDate
                  ? `Ends ${format(new Date(c.subscriptionEndDate), "MMM d, yyyy")}`
                  : "No renewal date"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <span>
              {c.card
                ? `${c.card.brand.toUpperCase()} •••• ${c.card.last4} (${c.card.expMonth}/${String(c.card.expYear).slice(-2)})`
                : "No card on file"}
            </span>
          </div>
          {c.stripe?.currentAmount != null && (
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-muted-foreground" />
              <span>{formatMoney(c.stripe.currentAmount, c.stripe.currentCurrency)} / {c.plan === "yearly" ? "year" : "month"}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setUpdateCardOpen(true)} data-testid={`update-card-${c.childId}`}>
            Update card
          </Button>
          {isActive && !isCancelling && (
            <Button variant="outline" size="sm" onClick={() => setPlanOpen(true)} data-testid={`change-plan-${c.childId}`}>
              Change plan
            </Button>
          )}
          {isCancelling ? (
            <Button variant="outline" size="sm" onClick={() => reactivateMutation.mutate()} disabled={reactivateMutation.isPending}>
              {reactivateMutation.isPending ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
              Keep my subscription
            </Button>
          ) : isActive ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                capturePostHogEvent("parent_cancel_dialog_opened", { childId: c.childId, status: c.subscriptionStatus });
                setCancelOpen(true);
              }}
              data-testid={`cancel-${c.childId}`}
            >
              Cancel
            </Button>
          ) : null}
          <InvoicesSheet childId={c.childId} />
        </div>
      </CardContent>

      <CancelSubscriptionDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        firstName={c.firstName}
        renewalDate={renewalDate}
        saveOfferAvailable={!c.retentionSaveOfferShown}
        pauseOfferAvailable={!c.retentionPauseOfferShown}
        onSaveOffer={() => saveOfferMutation.mutate()}
        onPause={(months) => pauseMutation.mutate(months)}
        onConfirm={(reason, otherDetails) => cancelMutation.mutate({ reason, otherDetails })}
        submitting={cancelMutation.isPending || saveOfferMutation.isPending || pauseMutation.isPending}
      />
      <ChangePlanDialog open={planOpen} onOpenChange={setPlanOpen} childId={c.childId} currentPlan={c.plan ?? "monthly"} firstName={c.firstName} />
      <UpdateCardDialog open={updateCardOpen} onOpenChange={setUpdateCardOpen} childId={c.childId} firstName={c.firstName} />
    </Card>
  );
}

const CANCEL_REASONS = [
  "Too expensive / can't afford it right now",
  "Not using it enough",
  "Found another platform that works better",
  "Technical issues or problems with the platform",
  "Completed my studies / no longer need it",
  "Content doesn't match my needs",
  "Other",
];

type CancelStep = "save" | "pause" | "reason";

function CancelSubscriptionDialog({
  open, onOpenChange, firstName, renewalDate,
  saveOfferAvailable, pauseOfferAvailable,
  onSaveOffer, onPause, onConfirm, submitting,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  firstName: string;
  renewalDate: Date | null;
  saveOfferAvailable: boolean;
  pauseOfferAvailable: boolean;
  onSaveOffer: () => void;
  onPause: (months: 1 | 2 | 3) => void;
  onConfirm: (reason: string, otherDetails?: string) => void;
  submitting: boolean;
}) {
  // Pick the first available step on open.
  const firstStep: CancelStep = saveOfferAvailable ? "save" : pauseOfferAvailable ? "pause" : "reason";
  const [step, setStep] = useState<CancelStep>(firstStep);
  const [reason, setReason] = useState<string>("");
  const [otherDetails, setOtherDetails] = useState("");

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setStep(firstStep);
      setReason("");
      setOtherDetails("");
    }
    onOpenChange(v);
  };

  const skipToNext = () => {
    if (step === "save") setStep(pauseOfferAvailable ? "pause" : "reason");
    else if (step === "pause") setStep("reason");
  };

  const canConfirm = reason.length > 0 && (reason !== "Other" || otherDetails.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {step === "save" && (
          <>
            <DialogHeader>
              <DialogTitle>Before you go, take 40% off?</DialogTitle>
              <DialogDescription>
                We hate to see {firstName} leave. Apply 40% off the next bill and stay. One-time offer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="ghost" onClick={skipToNext}>No thanks</Button>
              <Button onClick={onSaveOffer} disabled={submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Apply 40% off
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "pause" && (
          <>
            <DialogHeader>
              <DialogTitle>Take a break instead?</DialogTitle>
              <DialogDescription>
                Pause {firstName}'s subscription for up to 3 months. We won't charge you and you can resume any time.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((m) => (
                <Button
                  key={m}
                  variant="outline"
                  onClick={() => onPause(m as 1 | 2 | 3)}
                  disabled={submitting}
                  className="flex flex-col h-auto py-3"
                  data-testid={`pause-${m}-month`}
                >
                  <span className="text-lg font-semibold">{m}</span>
                  <span className="text-xs text-muted-foreground">{m === 1 ? "month" : "months"}</span>
                </Button>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={skipToNext}>No thanks, cancel</Button>
            </DialogFooter>
          </>
        )}

        {step === "reason" && (
          <>
            <DialogHeader>
              <DialogTitle>Cancel {firstName}'s subscription?</DialogTitle>
              <DialogDescription>
                {renewalDate
                  ? `${firstName} keeps full access until ${format(renewalDate, "MMM d, yyyy")}. After that their account becomes inactive.`
                  : `${firstName} keeps access until the end of the current billing period.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="cancel-reason">Why are you cancelling?</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger id="cancel-reason" data-testid="cancel-reason">
                    <SelectValue placeholder="Pick a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Helps us improve. Required.</p>
              </div>
              {reason === "Other" && (
                <div className="space-y-1.5">
                  <Label htmlFor="cancel-details">Tell us more</Label>
                  <Textarea
                    id="cancel-details"
                    value={otherDetails}
                    onChange={(e) => setOtherDetails(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder="What's going on?"
                    data-testid="cancel-details"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Keep subscription</Button>
              <Button
                variant="destructive"
                onClick={() => onConfirm(reason, reason === "Other" ? otherDetails : undefined)}
                disabled={!canConfirm || submitting}
                data-testid="cancel-confirm"
              >
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Cancel subscription
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChangePlanDialog({ open, onOpenChange, childId, currentPlan, firstName }: {
  open: boolean; onOpenChange: (v: boolean) => void; childId: string; currentPlan: string; firstName: string;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [plan, setPlan] = useState<"monthly" | "yearly">(currentPlan === "yearly" ? "monthly" : "yearly");
  const m = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/parent/billing/subscriptions/${childId}/change-plan`, { plan });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      toast({ title: "Plan changed", description: `${firstName} is now on the ${plan} plan.` });
      onOpenChange(false);
    },
    onError: (e: any) => toast({ title: "Plan change failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change {firstName}'s plan</DialogTitle>
          <DialogDescription>Switch between monthly and yearly. Stripe applies a proration on the next invoice.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>New plan</Label>
          <Select value={plan} onValueChange={(v) => setPlan(v as any)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="yearly">Yearly (saves ~16%)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending || plan === currentPlan}>
            {m.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Switch to {plan}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateCardDialog({ open, onOpenChange, childId, firstName }: {
  open: boolean; onOpenChange: (v: boolean) => void; childId: string; firstName: string;
}) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchSecret = async () => {
    setLoading(true);
    try {
      const r = await apiRequest("POST", "/api/parent/billing/setup-intent", { childId });
      const json = await r.json();
      setClientSecret(json.clientSecret);
    } catch (e: any) {
      toast({ title: "Could not initialise", description: e.message, variant: "destructive" });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (v && !clientSecret) fetchSecret(); else if (!v) setClientSecret(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update card for {firstName}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Stripe processes the card. We never see the number.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : clientSecret && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <UpdateCardForm onClose={() => onOpenChange(false)} childId={childId} />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function UpdateCardForm({ onClose, childId }: { onClose: () => void; childId: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (error) throw new Error(error.message ?? "Card setup failed");
      const pmId = setupIntent?.payment_method as string | undefined;
      if (!pmId) throw new Error("Stripe did not return a payment method");
      await apiRequest("POST", "/api/parent/billing/set-default-pm", { childId, paymentMethodId: pmId });
      qc.invalidateQueries({ queryKey: ["/api/parent/billing"] });
      toast({ title: "Card updated" });
      onClose();
    } catch (err: any) {
      toast({ title: "Could not save card", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement />
      <Button type="submit" disabled={submitting || !stripe} className="w-full">
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Save card
      </Button>
    </form>
  );
}

function InvoicesSheet({ childId }: { childId: string }) {
  const { data, isLoading } = useQuery<{ invoices: Invoice[] }>({
    queryKey: [`/api/parent/billing/invoices?childId=${childId}`],
  });
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`invoices-${childId}`}>
          <FileText className="w-3.5 h-3.5 mr-1.5" />
          Invoices
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Invoice history</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-2">
          {isLoading ? (
            <div className="space-y-2">
              {[0,1,2].map((i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : !data || data.invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No invoices yet.</p>
          ) : (
            data.invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between rounded-md border p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{inv.number ?? inv.id.slice(0, 12)}</p>
                  <p className="text-xs text-muted-foreground">
                    {inv.created ? format(new Date(inv.created), "MMM d, yyyy") : "–"} · {inv.status}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold">{formatMoney(inv.amountPaid, inv.currency)}</p>
                  {inv.pdfUrl && (
                    <a href={inv.pdfUrl} target="_blank" rel="noopener" className="text-xs text-primary inline-flex items-center gap-1 mt-1">
                      PDF <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AddChildDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", grade: "6", plan: "monthly" as "monthly" | "yearly" });
  const submit = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", "/api/parent/checkout/add-child", {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        grade: Number(form.grade),
        plan: form.plan,
      });
      return r.json();
    },
    onSuccess: (data: { checkoutUrl: string }) => {
      capturePostHogEvent("parent_add_child_redirected_to_checkout", { grade: Number(form.grade), plan: form.plan });
      window.location.href = data.checkoutUrl;
    },
    onError: (e: any) => toast({ title: "Could not add child", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a child</DialogTitle>
          <DialogDescription>Create an account for another child and start their subscription. You will be redirected to Stripe to complete payment.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Child's login email</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <p className="text-xs text-muted-foreground">Used for their student login. We will email a password setup link.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="grade">Grade</Label>
              <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">Grade 4</SelectItem>
                  <SelectItem value="5">Grade 5</SelectItem>
                  <SelectItem value="6">Grade 6</SelectItem>
                  <SelectItem value="7">Grade 7</SelectItem>
                  <SelectItem value="11">Grade 11</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan">Plan</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v as any })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submit.isPending}>
              {submit.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Continue to checkout
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
